/**
 * CSQ: Sticky filter bar for collection & search pages.
 * - Measures the sticky header and filter bar and exposes them as CSS variables.
 * - Adds an `is-stuck` class to the bar while it's pinned.
 * - Keeps the result count in sync after AJAX filtering.
 * - After filters/sorting change, scrolls so the first results sit just below the bar.
 */
(() => {
  if (window.csqStickyFilters) return;
  window.csqStickyFilters = true;

  const root = document.documentElement;
  let headerOffset = 0;
  let toolbarHeight = 0;
  let ticking = false;
  let pendingScroll = false;

  const getToolbar = () => document.querySelector('[data-csq-sticky-toolbar]');
  const getHeader = () => document.querySelector('store-header[data-is-sticky="true"]');
  const getResults = () => document.getElementById('filter-results');
  const scrollIsLocked = () => document.body.classList.contains('overflow-hidden')
    || document.body.classList.contains('fixed');

  function measureHeader() {
    const header = getHeader();
    // The header section is sticky at top: -1px, so its visible height is offsetHeight - 1.
    const value = header ? Math.max(0, header.offsetHeight - 1) : 0;
    if (value === headerOffset && root.style.getPropertyValue('--csq-header-offset')) return;
    headerOffset = value;
    root.style.setProperty('--csq-header-offset', `${headerOffset}px`);
  }

  function measureToolbar() {
    const toolbar = getToolbar();
    const value = toolbar ? toolbar.offsetHeight : 0;
    if (value === toolbarHeight && root.style.getPropertyValue('--csq-toolbar-height')) return;
    toolbarHeight = value;
    root.style.setProperty('--csq-toolbar-height', `${toolbarHeight}px`);
  }

  function updateStuck() {
    ticking = false;
    // Re-measure on scroll too: the header can animate its height (e.g. menu row hiding), and
    // ResizeObserver alone can miss the final size. Writes only happen when a value changes.
    measureHeader();
    measureToolbar();
    const toolbar = getToolbar();
    if (!toolbar) return;
    const { top } = toolbar.getBoundingClientRect();
    toolbar.classList.toggle('is-stuck', window.scrollY > 0 && top <= headerOffset + 1);
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateStuck);
    }
  }

  function syncState() {
    const state = document.querySelector('[data-csq-filter-state]');
    if (!state) return;
    document.querySelectorAll('[data-csq-result-count]').forEach((el) => {
      if (state.dataset.countLabel) el.textContent = state.dataset.countLabel;
    });
  }

  function scrollToResults() {
    const results = getResults();
    if (!results) return;
    const stickyBottom = headerOffset + toolbarHeight;
    const resultsTop = results.getBoundingClientRect().top;

    // Only scroll when the top of the results is hidden behind the sticky bars (or above the fold)
    if (resultsTop < stickyBottom) {
      window.scrollTo({
        top: resultsTop + window.scrollY - stickyBottom - 16,
        behavior: 'smooth'
      });
    }
  }

  function requestScrollToResults() {
    if (scrollIsLocked()) {
      // Mobile filter drawer is open; scroll once it closes.
      pendingScroll = true;
    } else {
      requestAnimationFrame(scrollToResults);
    }
  }

  function init() {
    const toolbar = getToolbar();
    if (!toolbar) return;

    updateStuck();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => updateStuck());
      const header = getHeader();
      if (header) ro.observe(header);
      ro.observe(toolbar);
    } else {
      window.addEventListener('resize', () => {
        measureHeader();
        measureToolbar();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    // #filter-results is replaced by the theme every time filters or sorting change.
    const results = getResults();
    if (results) {
      new MutationObserver(() => {
        syncState();
        requestScrollToResults();
      }).observe(results, { childList: true });
    }

    // Run a pending scroll once the mobile drawer closes and the page is unlocked.
    new MutationObserver(() => {
      if (pendingScroll && !scrollIsLocked()) {
        pendingScroll = false;
        setTimeout(scrollToResults, 50);
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    syncState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init when the section is reloaded.
  document.addEventListener('shopify:section:load', () => updateStuck());
})();